"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Autocomplete, Box, TextField } from "@/components/controls/base-controls";
import { useDebounce } from "@/core/hooks/use-debounce";
import { type AirportSearchData, airportSearchSchema } from "@/core/schemas/airport.schema";
import { dalFormatErrorMessage } from "@/core/dal/helper";
import AirportsService from "@/core/services/airports.service";
import ServiceRegistry from "@/core/services/service-registry";
import type { Airport } from "@/core/types/airport.type";
import { useLoadingStore } from "@/core/utils/loading.store";

// 🔑 Define interface properties accepting context from parent dialog
type AirportSearchFormProps = {
  selectedAirport: Airport | undefined,
  onAirportSelect: (value: Airport | undefined, shouldClose?: boolean) => void
}

const getLabel = (a: Airport | undefined) => a ? `${a.code} ${a.name}, ${a.city}, ${a.region}` : "";

const AirportSearchForm = ({selectedAirport, onAirportSelect }: AirportSearchFormProps) => {
    const [open, setOpen] = useState(false);
    const [airports, setAirports] = useState<readonly Airport[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [canFetch, setCanFetch] = useState(false);
    const lastSearchTerm = useRef("");

    const t = useTranslations();
    const { isLoading } = useLoadingStore();
    const debouncedSearchTerm = useDebounce(inputValue, 300);

    const { control, handleSubmit, formState: { errors } } = useForm<AirportSearchData>({
        resolver: zodResolver(airportSearchSchema),
        defaultValues: { 
            // 🔑 Seed form with the pre-selected option object on mount
            departure_airport: selectedAirport || null
         },
    });

    // Sync internal state when the selectedAirport prop changes (e.g. from parent or dialog reuse)
    useEffect(() => {
      if (selectedAirport) {
        setAirports([selectedAirport]);
        setInputValue(getLabel(selectedAirport));
      } else {
        setAirports([]);
        setInputValue("");
      }
      setCanFetch(false); // 🔑 Prevent immediate fetch when prop-driven sync occurs
    }, [selectedAirport]);

    // Fetch results whenever the debounced search term changes
  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const currentLabel = getLabel(selectedAirport);
    const isExactMatch = debouncedSearchTerm.trim().toLowerCase() === currentLabel.trim().toLowerCase();

    if (!canFetch || debouncedSearchTerm.trim().length < 3 || isExactMatch) {
      if (debouncedSearchTerm.trim().length < 3) {
        setAirports(selectedAirport ? [selectedAirport] : []);
      }
      setIsFetching(false);
      lastSearchTerm.current = debouncedSearchTerm;
      return;
    }

    const fetchAirports = async() => {
      setIsFetching(true);
      lastSearchTerm.current = debouncedSearchTerm;
      const airportsService = ServiceRegistry.get(AirportsService);

      // 🔑 Sanitize the query to remove commas which may trigger multi-attribute 
      // queries on the backend that the dev database schema doesn't support.
      const sanitizedQuery = debouncedSearchTerm.trim().replace(/,/g, "");

      const result = await airportsService.getAirports(
          { q: sanitizedQuery }, 
          controller.signal // 👈 Attaches the abort signal to the API layer
      );
      
      if (!active) return;

      if (result.success) {
        setAirports(result.data.items || []);
      } else if (result.error.type !== "request-canceled") {
        console.error("Failed to fetch airports:", dalFormatErrorMessage(result.error));
      }

      setIsFetching(false);
    }

    fetchAirports();

    // Cleanup to prevent race conditions if requests finish out of order
    return () => { 
        active = false;
        controller.abort();
    };
  }, [debouncedSearchTerm, canFetch, selectedAirport]);

  return (
    <Box
      component="form"
      noValidate aria-busy={isLoading} aria-describedby="data-processing"
      style={{ display: "flex", flexDirection: "column", flex: 1 }}
      onSubmit={handleSubmit((data) => console.log("Valid data:", data))}>
        <Controller
            name="departure_airport"
            control={control}
            render={({ field: { onChange, value } }) => {
                const autocompleteValue = (value as Airport | null) || null;

                // Only show loading if the input is long enough and we are actually allowed to fetch
                const isWaiting = canFetch && (inputValue.trim().length >= 3 && (
                    inputValue.trim() !== debouncedSearchTerm.trim() || 
                    debouncedSearchTerm !== lastSearchTerm.current ||
                    isFetching
                ));
                const isDropdownAllowedToOpen = open && (inputValue.trim().length >= 3 || airports.length > 0);

                return (
                <Autocomplete<Airport, false, false, false>
                    open={isDropdownAllowedToOpen} 
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    options={airports}
                    value={autocompleteValue}
                    // Sync text input to state for debouncing
                    inputValue={inputValue}

                    freeSolo={false}         // 🔑 Forces rigid alignment with your strict schema entries
                    disableClearable={false} // 🔑 Explicitly allows the native 'X' close button to map its listeners
                    loading={isWaiting}
            
                    onInputChange={(_, newInputValue, reason) => {
                      setInputValue(newInputValue);
                      
                      // 🔑 Only allow fetching if the change was driven by user typing
                      setCanFetch(reason === "input");

                      if (reason === "clear") {
                        setAirports([]);
                      }
                    }}

                    // Prevent matching logic in MUI since database handles filtering
                    filterOptions={(x) => x}
                    getOptionLabel={(option) => getLabel(option)}
                    isOptionEqualToValue={(option, val) => option?.id === val?.id}
                    slotProps={{
                        listbox: {
                            sx: { 
                                maxHeight: { xs: "72svh", md: "17em" } // Extends further on mobile screens
                            }
                        }
                    }}

                    // 🔑 Update parent state and automatically shut dialog panel on selection
                    onChange={(_, newValue, reason) => {
                        onChange(newValue);     // Syncs data inside React Hook Form state
                        setCanFetch(false);    // Block further fetching once a selection is made

                        if (reason === "clear" || !newValue) {
                          setInputValue("");
                          setAirports([]); 
                          onAirportSelect(undefined, false);
                          setCanFetch(false);

                          return;
                        }

                        onAirportSelect(newValue, true); // Fires parent close callback pipeline
                    }} 

                    noOptionsText={
                        isWaiting ? null : (
                          <Box sx={{ padding: "12px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}> 
                            <span style={{ fontSize: "1.2rem" }}>✈️❌</span> 
                            <strong>{t("airport.select.noneFound")}</strong> 
                            <span style={{ fontSize: "0.85rem", color: "#888" }}> 
                              {t("airport.select.noMatching", { inputValue })} 
                            </span> 
                          </Box> 
                        )
                    }

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus // 🔑 Forces the input field to take active focus immediately on mount
                            fullWidth
                            variant="filled"
                            label={t("airport.select.search")}
                            placeholder={t("airport.select.placeholder")}
                            error={!!errors.departure_airport}
                            helperText={errors.departure_airport ? String(errors.departure_airport.message) : undefined}

                            // 🔑 THE TRUE TYPE-SAFE MUI v6 SYNTAX:
                            slotProps={{ 
                                ...params.slotProps, // 1. Spreads top-level slot structures safely
                                htmlInput: { 
                                ...params.slotProps?.htmlInput, // 2. 💥 CRITICAL: Spreads MUI's underlying internal refs/keys
                                maxLength: 80 // 3. Safe to inject your custom rules here now!
                                } 
                            }}
                        />
                    )}
                />
            )}}
        />
    </Box>
  );
}

export default AirportSearchForm;
